<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import VuePdfEmbed, { type VuePdfEmbedMethods } from 'vue-pdf-embed';
import { pdfSource } from '@/utils/pdf-source.util';
import { useResizeObserver } from '@vueuse/core';

import ProProfileService from '@/core/conversations/pro-profile/pro-profile.service';
import DownloadResumeButton from '@/components/Conversations/Resume/DownloadResumeButton.vue';
import { useSignedResumeUrl } from '@/composables/useSignedResumeUrl';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import { ErrorService } from '@/core/shared/errors/error.service';
import { debounce, sleep } from 'radash';
import CopilotScoreBlurb from '@/components/Conversations/Copilot/CopilotScoreBlurb.vue';
import CopilotScoreBlurbPlaceholder from '@/components/Conversations/Copilot/CopilotScoreBlurbPlaceholder.vue';
import ResumePageFrame from '@/components/Conversations/Resume/ResumePageFrame.vue';
import ApplicationScoreService from '@/core/conversations/application-score/application-score.service';
import { hasApplicationScore } from '@/core/conversations/application-score/application-score.util';
import { getJobTitle } from '@/core/sourcing/utils/job';
import { toPdfFileName } from '@/utils/strings/pdf-file-name.util';
import type { ATSApplicationScoringProScoring } from '@factoryfixinc/ats-interfaces';
import ProjectService from '@/core/shared/project/project.service';
import ProjectCopilotConfigurationUtils, {
  DEFAULT_COPILOT_NAME,
} from '@/utils/project/project-copilot-configuration.utils';

const proProfileService = new ProProfileService();
const applicationScoreService = new ApplicationScoreService();
const projectService = new ProjectService();

const isLoading = ref(true);
const fileRef = ref<VuePdfEmbedMethods | null>(null);
const pdfWidth = ref(0);
const scoreDetails = ref<ATSApplicationScoringProScoring>();
const scoreDetailsApplicationId = ref<number>();
const aiRecruiterName = ref<string>(DEFAULT_COPILOT_NAME);

const proProfileResumeUrl = computed(() => proProfileService.selectedProProfile?.pro?.resumeUrl);
const {
  signedUrl: signedResumeUrl,
  isLoading: signedUrlLoading,
  error: signedUrlError,
} = useSignedResumeUrl(proProfileResumeUrl);

watch(signedUrlError, (newError) => {
  if (newError) handleError();
});
const selectedApplication = computed(() => proProfileService.selectedProApplication);
const isScoreDetailPending = computed(
  () =>
    !!selectedApplication.value && scoreDetailsApplicationId.value !== selectedApplication.value.id,
);
const isScoreCardExpected = computed(() => hasApplicationScore(selectedApplication.value?.score));
const chips = computed(() => ApplicationScoreService.getScoreChipsInfo(scoreDetails.value));
const formattedScore = computed(() =>
  ApplicationScoreService.formatScore(scoreDetails.value?.score),
);

const jobTitle = computed(() => {
  if (!selectedApplication.value?.job) {
    return '';
  }

  return getJobTitle(selectedApplication.value.job, false).displayTitle;
});

const selectedApplicationJobId = computed(() => selectedApplication.value?.jobId);

const downloadResume = () => {
  if (!fileRef.value) {
    return;
  }

  try {
    const { nameFirst = '', nameLast = '' } = proProfileService.selectedProProfile || {};
    const name = `${nameFirst}_${nameLast}_resume`;
    fileRef.value.download(toPdfFileName(name));
    TrackingService.trackAction(TrackingActionName.RESUME_TAB_RESUME_DOWNLOADED);
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to download resume. Please try again later.');
  }
};

function handleError() {
  isLoading.value = false;
  SnackbarService.critical('Error loading resume. Please try again later.');
}

function _setupPdfDimensions() {
  const pdfWrapper = document.getElementById('pdf-wrapper');

  if (pdfWrapper) {
    pdfWidth.value = pdfWrapper.offsetWidth;
  }
}
const setupPdfDimensions = debounce({ delay: 300 }, _setupPdfDimensions);

watch(proProfileResumeUrl, (newValue) => {
  if (!newValue) {
    isLoading.value = false;
  }
});

watch(
  selectedApplication,
  async (application) => {
    if (!application) {
      return;
    }

    let details: ATSApplicationScoringProScoring | undefined;
    try {
      details = await applicationScoreService.getScoreDetail(application.id);
    } finally {
      if (selectedApplication.value?.id === application.id) {
        scoreDetails.value = details;
        scoreDetailsApplicationId.value = application.id;
      }
    }
  },
  { immediate: true },
);

watch(
  selectedApplicationJobId,
  async (newJobId, oldJobId) => {
    let currentProject = null;

    if (newJobId && newJobId !== oldJobId) {
      currentProject = await projectService.getLocalProjectByJobId(newJobId);
    }

    aiRecruiterName.value = ProjectCopilotConfigurationUtils.getCopilotName(currentProject);
  },
  { immediate: true },
);

onMounted(async () => {
  setupPdfDimensions();

  // observe pdf wrapper for resizes
  const pdfWrapper = document.getElementById('pdf-wrapper');
  useResizeObserver(pdfWrapper, setupPdfDimensions);

  // let's wait some milliseconds before clearing the spinner if there is no resume
  await sleep(250);

  if (!proProfileResumeUrl.value) {
    isLoading.value = false;
  }
});
</script>

<template>
  <div class="resume h-full w-full overflow-y-scroll p-4">
    <!-- Copilot Score Blurb -->
    <CopilotScoreBlurbPlaceholder v-if="isScoreDetailPending && isScoreCardExpected" />
    <CopilotScoreBlurb
      v-else-if="
        !isScoreDetailPending &&
        hasApplicationScore(scoreDetails?.score) &&
        scoreDetails.summary &&
        scoreDetails.jobApplicationId
      "
      :show-visible-only-to-you="false"
      :show-copilot-chip="false"
      :score="formattedScore"
      :content="scoreDetails.summary"
      :chips="chips"
      :application-id="scoreDetails.jobApplicationId"
      :score-version="scoreDetails.version"
      :job-title="jobTitle"
      :ai-recruiter-name="aiRecruiterName"
    />

    <ResumePageFrame v-if="proProfileResumeUrl" id="pdf-wrapper" :loading="isLoading">
      <Transition name="fade">
        <div v-if="pdfWidth && signedResumeUrl">
          <DownloadResumeButton
            class="absolute z-50 float-right mr-4 mt-4"
            @click="downloadResume"
          />
          <VuePdfEmbed
            :source="pdfSource(signedResumeUrl)"
            ref="fileRef"
            :width="pdfWidth"
            class="sensitive"
            @rendered="isLoading = false"
            @loading-failed="handleError"
            @rendering-failed="handleError"
          />
        </div>
      </Transition>
    </ResumePageFrame>

    <template v-else>
      <ResumePageFrame v-if="isLoading || signedUrlLoading" loading />
      <template v-else>
        <div class="flex h-full flex-col items-center justify-center">
          <img src="@/assets/svg/no-resume.svg" alt="No resume" />
          <p class="mb-2 mt-4 text-sm font-bold text-shade-800">No resume available</p>
          <p class="w-60 text-center text-sm text-shade-800">
            This candidate doesn't<br />have resume yet.
          </p>
        </div>
      </template>
    </template>
  </div>
</template>
